import type { CSSProperties } from 'react'
import type { BorderEdge, SlideElement } from '~/design-system/components/DeckGen/types/presentation'
import { shadowsToCss } from '~/design-system/components/DeckGen/utils/boxShadow'
import { composeFilter } from '~/design-system/components/DeckGen/utils/blur'

/**
 * Shared style resolution for rendering a `SlideElement`'s preserved generation fidelity — used by every
 * render path (the in-editor `SlideElement`, the `ElementVisual` drag ghost, and the present/static view)
 * so the element looks identical everywhere. Kept tiny and pure.
 */

/**
 * The CSS `border-radius` for a shape. Per-corner `cornerRadii` ([tl, tr, br, bl]) wins when present (each
 * corner clamped to half the shorter side), else the `uniformRadius` the caller already resolved (which is
 * what circles + the live radius-handle drag use). Circles pass '50%' themselves and shouldn't call this.
 */
export function shapeBorderRadius(
  element: SlideElement,
  width: number,
  height: number,
  uniformRadius: number,
): string | number {
  if (element.cornerRadii) {
    const cap = Math.floor(Math.min(width, height) / 2)
    return element.cornerRadii.map((r) => `${Math.max(0, Math.min(r, cap))}px`).join(' ')
  }
  return uniformRadius
}

/** `transform: rotate()` for a rotated element, or undefined. Applied on the element's visual box (its
 *  transform-origin is the centre), matching how the slide was generated. */
export function rotationTransform(element: SlideElement): string | undefined {
  return element.rotation ? `rotate(${element.rotation}deg)` : undefined
}

/** The element's CSS filter / backdrop-filter — the preserved generation strings (brightness, frosted-glass,
 *  etc.) composed with the structured `layerBlur` (→ filter) and `backdropBlur` (→ backdrop-filter), which
 *  are the editable source of truth and win over any blur in the preserved string. Empty object when none. */
export function filterStyle(element: SlideElement): CSSProperties {
  const filter = composeFilter(element.filter, element.layerBlur)
  const backdropFilter = composeFilter(element.backdropFilter, element.backdropBlur)
  if (!filter && !backdropFilter) return {}
  return {
    filter,
    backdropFilter,
    WebkitBackdropFilter: backdropFilter,
  }
}

/** True when a `background` value is a background-IMAGE fill — a gradient or a `url()`/pattern — and so
 *  belongs in the `backgroundImage` longhand rather than `backgroundColor`. */
function isBackgroundImageValue(bg?: string): bg is string {
  return typeof bg === 'string' && (bg.includes('gradient') || bg.includes('url('))
}

/** A raster/file `url()` fill carried as a `background` STRING (a gradient is sized by its own stops) —
 *  it needs cover/center/no-repeat or the browser tiles it at native size. Empty object for gradients. */
function rasterUrlSizing(bg: string): CSSProperties {
  return bg.includes('url(') && !bg.includes('gradient(')
    ? { backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }
    : {}
}

/**
 * The CSS background for a SLIDE, resolved to LONGHANDS only (`backgroundColor` / `backgroundImage`) —
 * never the `background` shorthand. A gradient is a `background-image`, so mixing the `background`
 * shorthand with a sibling `backgroundImage: undefined` longhand (as the slide stages used to) makes
 * React clear the gradient the shorthand just set, dropping the background entirely. Resolving to
 * longhands here keeps gradient / solid / image-fill backgrounds all rendering reliably.
 */
export function slideBackgroundStyle(opts: {
  background?: string
  fillImage?: string
  usesImageFill: boolean
}): CSSProperties {
  const { background, fillImage, usesImageFill } = opts
  if (usesImageFill && fillImage) {
    return {
      backgroundImage: `url("${fillImage}")`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }
  }
  const bg = background ?? '#ffffff'
  return isBackgroundImageValue(bg) ? { backgroundImage: bg, ...rasterUrlSizing(bg) } : { backgroundColor: bg }
}

/**
 * The CSS background for a SHAPE element, resolved to LONGHANDS only — same reasoning as
 * {@link slideBackgroundStyle}: a gradient fill lives in `background-image`, so the old `background`
 * shorthand + sibling `backgroundImage: undefined` longhand cleared gradient fills on re-render. Handles
 * solid color, gradient, and image fill (an image paints over an optional solid base color).
 */
export function shapeBackgroundStyle(element: SlideElement): CSSProperties {
  const usesImageFill =
    element.shapeFillMode === 'image' || (element.shapeFillMode == null && Boolean(element.fillImage))
  const bg = element.background
  const isImage = isBackgroundImageValue(bg)
  if (usesImageFill && element.fillImage) {
    return {
      backgroundColor: bg && !isImage ? bg : undefined,
      backgroundImage: `url("${element.fillImage}")`,
      // 'fill' = stretch to the box (no CSS background-size keyword for it → explicit 100% 100%).
      backgroundSize: element.imageFit === 'fill' ? '100% 100%' : (element.imageFit ?? 'cover'),
      backgroundPosition: element.imagePosition ?? 'center',
      backgroundRepeat: 'no-repeat',
    }
  }
  if (!isImage) return { backgroundColor: bg }
  // A TILING pattern (grid/lines/dots) carries its repeat + size so it tiles across the box instead of the
  // single-image cover/no-repeat — a tiny tile shown once on a 1920px slide reads as empty.
  if (element.backgroundRepeat || element.backgroundSize) {
    return {
      backgroundImage: bg,
      backgroundRepeat: element.backgroundRepeat ?? 'no-repeat',
      backgroundSize: element.backgroundSize ?? 'auto',
      backgroundPosition: '0 0',
    }
  }
  return { backgroundImage: bg, ...rasterUrlSizing(bg) }
}

/** The element's effective `box-shadow` CSS — serialized from the structured `boxShadows` stack when present
 *  (the editable source of truth), else the preserved `boxShadow` string. Spread into the shape style. */
export function effectiveBoxShadow(element: SlideElement): string | undefined {
  if (element.boxShadows && element.boxShadows.length > 0) return shadowsToCss(element.boxShadows)
  return element.boxShadow
}

/** One border edge serialized to a CSS `border-<side>` shorthand. Width 0 (or a missing edge) → '0' so the
 *  side renders no line regardless of color/style. */
function borderEdgeToCss(edge?: BorderEdge): string {
  if (!edge || !edge.width) return '0'
  return `${edge.width}px ${edge.style ?? 'solid'} ${edge.color}`
}

/**
 * Border longhands for a shape, resolved to the four `borderTop/Right/Bottom/Left` shorthands (always all
 * four, so the style object's shape stays stable across renders — no shorthand↔longhand churn that would
 * strand a stale edge). Per-side `element.border` wins when present (each edge independent; an absent side =
 * no line); otherwise the uniform `stroke*` (with the historical 2px / #93c5fd defaults) paints all four
 * sides. Spread into the shape style alongside `shapeBackgroundStyle`. `border-radius` is independent and
 * set separately by the caller.
 */
export function shapeBorderStyle(element: SlideElement): CSSProperties {
  const { border } = element
  if (border) {
    return {
      borderTop: borderEdgeToCss(border.top),
      borderRight: borderEdgeToCss(border.right),
      borderBottom: borderEdgeToCss(border.bottom),
      borderLeft: borderEdgeToCss(border.left),
    }
  }
  // Center/outside alignment is painted by the stroke overlay (see `strokeOverlayStyle`), so the box itself
  // draws no border — otherwise the stroke would double up.
  if (element.strokeAlign === 'center' || element.strokeAlign === 'outside') {
    return { borderTop: '0', borderRight: '0', borderBottom: '0', borderLeft: '0' }
  }
  const uniform = borderEdgeToCss(uniformStrokeEdge(element))
  return { borderTop: uniform, borderRight: uniform, borderBottom: uniform, borderLeft: uniform }
}

/** The effective uniform stroke as a single edge (the historical 2px / #93c5fd fallbacks), used by both the
 *  box border and the alignment overlay so they describe the same line. */
function uniformStrokeEdge(element: SlideElement): BorderEdge {
  return {
    width: element.strokeWidth ?? 2,
    color: element.strokeColor ?? '#93c5fd',
    style: element.strokeStyle ?? 'solid',
  }
}

/**
 * The absolutely-positioned overlay that paints a UNIFORM stroke for `center` / `outside` alignment — Figma
 * parity that real CSS borders (always inside a border-box) can't express, while still supporting
 * dashed/dotted and rounded corners. Rendered as a `position: relative` fill box's child; returns null when
 * the box border already handles the stroke (inside/undefined alignment, per-side `border`, or width 0).
 *
 * `radius` is the box's resolved border-radius. The overlay's box-border-box is expanded outward by the
 * stroke (half for center, full for outside) via negative inset, and the radius grows to match so the
 * rounded corners stay concentric. Circles ('50%') and per-corner strings are passed through unchanged.
 */
export function strokeOverlayStyle(element: SlideElement, radius: string | number): CSSProperties | null {
  if (element.border) return null
  const align = element.strokeAlign
  if (align !== 'center' && align !== 'outside') return null
  const edge = uniformStrokeEdge(element)
  if (!edge.width || edge.width <= 0) return null
  const grow = align === 'outside' ? edge.width : edge.width / 2
  let borderRadius: string | number = radius
  if (typeof radius === 'number') borderRadius = radius + grow
  else if (radius === '50%') borderRadius = '50%'
  // Per-corner radius string (from `cornerRadii`, e.g. '8px 8px 4px 4px'): grow EACH corner by the same
  // amount the box grew so the stroke stays concentric with the fill's corners.
  else borderRadius = radius.trim().split(/\s+/).map((p) => `${Math.max(0, (parseFloat(p) || 0) + grow)}px`).join(' ')
  return {
    position: 'absolute',
    inset: -grow,
    borderRadius,
    border: `${edge.width}px ${edge.style ?? 'solid'} ${edge.color}`,
    boxSizing: 'border-box',
    pointerEvents: 'none',
  }
}

/** The CSS properties that paint a gradient through a text element's glyphs, or null when it has none.
 *  Spread into the text style; the caller sets `color: transparent` alongside. */
export function textGradientStyle(element: SlideElement): CSSProperties | null {
  if (!element.textGradient) return null
  return {
    backgroundImage: element.textGradient,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  }
}
